import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Compass } from "lucide-react";
import Header from "@/sections/Header";
import FooterSection from "@/sections/Footer";
import { StatusLED } from "@/components/infra";
import { PathExplorer } from "@/components/careers/PathExplorer";
import { EarlyCareerBoard } from "@/components/careers/EarlyCareerBoard";
import { RoleGuides } from "@/components/careers/RoleGuides";
import { CareersCerts, CareersPathSteps } from "@/components/careers/CareersCerts";
import { SkillsGap } from "@/components/careers/SkillsGap";
import { ApplicationPrep } from "@/components/careers/ApplicationPrep";
import { JobAlertsForm } from "@/components/careers/JobAlertsForm";
import { EmployerSpotlights } from "@/components/careers/EmployerSpotlights";
import { ConsultationCta } from "@/components/careers/ConsultationCta";
import { EmployerPartners } from "@/sections/EmployerPartners";
import { openCalendly } from "@/lib/calendly";

const HUB_SECTIONS = [
  { id: "find-your-path", label: "Find your path" },
  { id: "early-career", label: "Early career" },
  { id: "role-guides", label: "Role guides" },
  { id: "certifications", label: "Certifications" },
  { id: "skills-gap", label: "Skills gap" },
  { id: "prep", label: "Application prep" },
  { id: "alerts", label: "Job alerts" },
  { id: "spotlights", label: "Employers" },
  { id: "employers", label: "For employers" },
  { id: "consultation", label: "Get help" },
];

const scrollToHash = (hash) => {
  const target = hash ? document.getElementById(hash.slice(1)) : null;
  if (target) target.scrollIntoView({ block: "start", behavior: "instant" });
};

export default function CareersPage() {
  const location = useLocation();
  useEffect(() => {
    document.title = "Careers Hub · DCYB";
    if (!location.hash) { window.scrollTo(0, 0); return undefined; }
    let active = true;
    document.fonts.ready.then(() => { if (active) scrollToHash(location.hash); });
    const settle = setTimeout(() => { if (active) scrollToHash(location.hash); }, 700);
    return () => { active = false; clearTimeout(settle); };
  }, [location.hash, location.search]);

  return (
    <main data-testid="careers-page" className="relative">
      <Header />
      <section data-testid="careers-hero" className="relative pt-[60px] pb-14 md:pt-[88px] md:pb-20 border-b hairline overflow-hidden bg-white">
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.45]" style={{ backgroundImage: "linear-gradient(to right, rgba(15,17,23,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,17,23,0.05) 1px, transparent 1px)", backgroundSize: "80px 80px", maskImage: "radial-gradient(900px 600px at 50% 30%, #000 30%, transparent 75%)", WebkitMaskImage: "radial-gradient(900px 600px at 50% 30%, #000 30%, transparent 75%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/" data-testid="careers-back-link" className="inline-flex items-center gap-2 font-mono-label text-[11px] text-[#5F6168] hover:text-[#0F1117] transition-colors mb-8">← Back to DCYB</Link>
          <div className="flex items-center gap-3 mb-8 fade-up"><span className="inline-block w-6 h-px chrome-bar" /><StatusLED color="#10B981" /><span className="font-mono-label text-[11px] text-[#0F1117]">Careers Hub · Digital Infrastructure</span></div>
          <h1 data-testid="careers-headline" className="font-display text-[2.1rem] sm:text-5xl lg:text-6xl xl:text-[4.5rem] leading-[1.04] font-extrabold text-[#0F1117] text-balance max-w-5xl fade-up delay-100">
            The industry is hiring.<br /><span className="text-chrome">Here's how you get in.</span>
          </h1>
          <p data-testid="careers-hero-copy" className="mt-8 max-w-3xl text-base sm:text-lg text-[#3D4049] leading-relaxed fade-up delay-200">
            Find the path that fits you, see the openings you can actually get, understand what each role really involves, close your skill gaps and get personal help from people who do the work. Every listing is real and sourced from the employer.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 fade-up delay-300">
            <a href="#find-your-path" data-testid="careers-hero-cta" className="group chrome-btn-dark chrome-btn-dark-glow inline-flex items-center gap-2.5 font-mono-label text-[12px] px-7 py-4"><Compass size={16} aria-hidden="true" />Find Your Path<ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></a>
            <button type="button" onClick={openCalendly} data-testid="careers-hero-consult-btn" className="chrome-btn inline-flex items-center gap-2.5 font-mono-label text-[12px] px-7 py-4"><span className="live-dot" aria-hidden />Get Help Entering the Industry</button>
          </div>
        </div>
      </section>
      <nav aria-label="Careers Hub sections" data-testid="careers-hub-nav" className="sticky top-[68px] md:top-[72px] z-30 bg-white/95 backdrop-blur border-b hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex gap-1 overflow-x-auto scrollbar-none py-2">
          {HUB_SECTIONS.map((section) => <a key={section.id} href={`#${section.id}`} data-testid={`careers-nav-${section.id}`} className="shrink-0 px-3 py-2 font-mono-label text-[10px] !tracking-normal text-[#3C4450] hover:text-[#0F141B] hover:bg-[#F0F2F4] transition-colors whitespace-nowrap">{section.label}</a>)}
        </div>
      </nav>
      <PathExplorer />
      <EarlyCareerBoard />
      <RoleGuides />
      <CareersCerts />
      <SkillsGap />
      <ApplicationPrep />
      <JobAlertsForm />
      <EmployerSpotlights />
      <EmployerPartners />
      <ConsultationCta />
      <CareersPathSteps />
      <section data-testid="careers-closer" className="bg-white border-b hairline">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-24 text-center">
          <p className="font-display text-2xl sm:text-4xl lg:text-5xl font-extrabold text-[#0F1117] leading-[1.1] sm:leading-[1.1] lg:leading-[1.1] tracking-tight text-balance">The next decade of digital infrastructure will be built by people. <span className="text-chrome">We&apos;re building them.</span></p>
        </div>
      </section>
      <FooterSection />
    </main>
  );
}
